/**
 * ISSO PARECE UMA HISTÓRIA, OU SÃO LETRAS SOLTAS? — `T4.2`, e a resposta é LOCAL.
 *
 * ── A FALA DELE, E O QUE ELA PEDE ───────────────────────────────────────────────────────────
 *
 * *"eu botei aleatoriamente aqui eu escrevi várias letras, vamos ver o que ele vai entender (…) a
 * parte do escreva história toda não funcionou"*. Ele digitou
 * `asdkjh askjdh askjdh qwoieu zxcmnb poiuyt lkjhgf mnbvcx` e recebeu um quadrinho, como se aquilo
 * fosse uma história. O defeito não é o roteirizador falhar: é ele **não falhar**.
 *
 * ── POR QUE ISTO ESTEVE DEZENOVE DIAS ESPERANDO DINHEIRO, E NÃO PRECISAVA ───────────────────
 *
 * A guarda que existia perguntava sobre o RESULTADO — *"saiu alguma fala?"*. Desde que a separação
 * de cenas ganhou queda para a quebra por regra, sempre sai: a quebra transforma qualquer linha numa
 * narração. A guarda não pode mais disparar.
 *
 * O nó que segurava este ponto concluiu, então, que distinguir `asdkjh` de *"A Bia perdeu o
 * caderno."* **exige entender as palavras**, e portanto exige o provedor de LLM — cujo crédito está
 * esgotado e é dinheiro dele. A conclusão vale para a pergunta difícil, e a pergunta aqui é outra e
 * muito mais fácil:
 *
 *   a pergunta difícil:  *isto é uma boa história?*        → precisa entender. Não é nossa, e este
 *                                                            épico decidiu por escrito não julgar a
 *                                                            escrita de uma criança.
 *   a pergunta fácil:    *isto tem alguma palavra que dá   → não precisa entender NADA. Precisa só
 *                         para pronunciar em português?*      saber onde ficam as vogais.
 *
 * `asdkjh` não é uma palavra ruim: é uma sequência que a boca não pronuncia. *"o gatu subil no
 * telhadu"* é cheia de erro e se pronuncia inteira. **A diferença entre as duas não está no sentido,
 * e é por isso que ela se mede sem LLM e sem rede.**
 *
 * ── A REGRA, E ELA É DE PROPÓSITO GROSSA ────────────────────────────────────────────────────
 *
 * Uma palavra é PRONUNCIÁVEL quando tem ao menos uma vogal e não tem cinco consoantes seguidas. O
 * texto parece história quando **metade ou mais** das suas palavras são pronunciáveis.
 *
 * A folga de metade é o que protege quem escreve `BRRRR! o carro passou` (uma palavra reprovada em
 * cinco) e quem escreve `kkkkk que engraçado`. Quem digita a fileira do teclado reprova quase todas.
 *
 * ── E ELA NUNCA RECUSA. ISSO É O MAIS IMPORTANTE DESTE ARQUIVO ──────────────────────────────
 *
 * O resultado desta função não fecha porta nenhuma: a tela PERGUNTA, e há um botão para seguir
 * assim mesmo. Um falso positivo custa um clique; uma recusa custaria a história de uma criança que
 * escreve diferente do que esta régua espera. As duas contas não se parecem, e por isso a régua
 * pode ser grossa: ela está errada para o lado barato.
 */

/** As vogais, com os acentos que o português usa — sem elas a régua reprovaria "avô" e "irmã". */
const VOGAIS = "aeiouáéíóúâêôàãõü";

/**
 * Quantas consoantes seguidas ainda cabem numa palavra pronunciável.
 *
 * ── ESTE NÚMERO ERA 4, E O TESTE DO LADO CARO O CORRIGIU ANTES DE ELE IR AO AR ──────────────
 *
 * Com 4, a régua reprovava **abstrato**, **monstro** e **instrução** — `bstr`, `nstr` e `nstr` são
 * quatro consoantes seguidas em português corrente. A régua que existe para não julgar a escrita de
 * uma criança estava reprovando o dicionário.
 *
 * Com 5, nenhuma palavra do português que eu consiga formar reprova (`transcrição` tem `nscr`, que
 * são quatro), e a fileira do teclado continua reprovando: `asdkjh` tem `sdkjh` e `asdfgh` tem
 * `sdfgh`, cinco cada. O teste que pegou isto (`os encontros consonantais do português passam`) é o
 * que justifica os outros: uma régua sem o lado caro medido é uma régua que reprova quem escreve.
 */
export const CONSOANTES_SEGUIDAS = 5;

/** Que fração das palavras precisa ser pronunciável para o texto parecer história. */
export const FRACAO_MINIMA = 0.5;

export type Veredicto = {
  parece: boolean;
  /** Quantas palavras foram lidas — zero quer dizer campo vazio, e campo vazio não é assunto daqui. */
  palavras: number;
  pronunciaveis: number;
  /** Até três exemplos do que reprovou, para a tela poder mostrar o que ela viu. */
  exemplos: string[];
};

/**
 * Tira acento para a régua olhar só a forma — "coração" e "coracao" reprovam ou passam juntas.
 *
 * A faixa é escrita com `\u0300-\u036f` e não com os caracteres em si: os sinais combinantes são
 * INVISÍVEIS num editor, e uma faixa de caracteres que ninguém vê é uma faixa que alguém apaga sem
 * perceber.
 */
function semAcento(palavra: string): string {
  return palavra.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function palavraPronunciavel(bruta: string): boolean {
  const p = bruta.toLowerCase();
  // `Array.from` e não o operador de espalhar: espalhar string exige `downlevelIteration` no alvo
  // deste projeto, e o `tsc` recusa. O mesmo laço, sem mudar a regra.
  const temVogal = Array.from(p).some((c) => VOGAIS.includes(c));
  if (!temVogal) return false;

  // A contagem de consoantes seguidas roda sobre a forma SEM acento, senão "ê" contaria como
  // consoante e "você" reprovaria.
  let seguidas = 0;
  for (const c of Array.from(semAcento(p))) {
    const ehLetra = c >= "a" && c <= "z";
    if (!ehLetra) {
      seguidas = 0;
      continue;
    }
    seguidas = VOGAIS.includes(c) ? 0 : seguidas + 1;
    if (seguidas >= CONSOANTES_SEGUIDAS) return false;
  }
  return true;
}

export function pareceHistoria(texto: string): Veredicto {
  // Só letras contam como palavra. Pontuação, número e emoji não reprovam nem aprovam ninguém:
  // "123" não é palavra mal escrita, é outra coisa, e contá-la como reprovada faria "São 3 gatos"
  // pesar pior do que deve.
  const palavras = (texto.match(/[\p{L}]+/gu) || []).filter(Boolean);
  if (palavras.length === 0) {
    // Campo vazio, ou só números e pontuação. Quem trata campo vazio é o `required` do formulário —
    // esta função não inventa uma segunda mensagem para o mesmo caso.
    return { parece: true, palavras: 0, pronunciaveis: 0, exemplos: [] };
  }

  const ruins: string[] = [];
  let bons = 0;
  for (const p of palavras) {
    if (palavraPronunciavel(p)) bons++;
    else if (ruins.length < 3) ruins.push(p);
  }

  return {
    parece: bons / palavras.length >= FRACAO_MINIMA,
    palavras: palavras.length,
    pronunciaveis: bons,
    exemplos: ruins,
  };
}
